// Settings modal (Unit 33, spec §11.4).
//
// Tabs:
//   * General   — UI language, default project language, comment templates.
//   * Health    — service checks + diagnostic bundle download.
//   * About     — version, log out, reset-database danger zone.
//   * Shortcuts — keyboard shortcut reference.
//   * Manual    — opens the standalone user manual (/manual.html) in a new tab.
//   * Developer — guided tour controls + pipeline tuning.
//
// Rendered as a backdrop modal from Layout and PaperReview; no own route.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import { apiFetch, apiJson } from "../api/client";
import { useSettings } from "../api/hooks";
import { cleanupTour } from "../api/tour";
import type { AppSettings } from "../api/types";
import { endTour } from "../components/GuidedTour";
import { RasterReference } from "../components/RasterReference";
import { TemplateManager } from "../components/TemplateManager";
import { changeLanguage } from "../i18n/i18n";
import { toast } from "../lib/toast";
import { useTourStore } from "../state/tourStore";
import { useDevStore } from "../state/devStore";

import "./Settings.css";

type Tab = "general" | "raster" | "health" | "about" | "shortcuts" | "manual" | "developer";

interface Props {
  onClose: () => void;
}

export function SettingsModal({ onClose }: Props) {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("general");
  const panelRef = useRef<HTMLDivElement>(null);

  // Close on Escape.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") onClose();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const TABS: { key: Tab; label: string }[] = [
    { key: "general", label: t("settings.tab_general") },
    { key: "raster", label: t("settings.tab_raster") },
    { key: "health", label: t("settings.tab_health") },
    { key: "shortcuts", label: t("settings.tab_shortcuts") },
    { key: "manual", label: t("settings.tab_manual") },
    { key: "developer", label: t("settings.developer_tab") },
    { key: "about", label: t("settings.tab_about") },
  ];

  return (
    <div
      className="s-body"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={t("settings.title")}
    >
      <div
        className={`s-panel${tab === "raster" ? " s-panel--wide" : ""}`}
        ref={panelRef}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="s-title">{t("settings.title")}</div>
        <button type="button" className="s-close" onClick={onClose} aria-label={t("common.close")}>
          ✕
        </button>

        <div className="s-tabs">
          {TABS.map(({ key, label }) => (
            <span
              key={key}
              className={`s-tab${tab === key ? " active" : ""}`}
              onClick={() => setTab(key)}
              role="tab"
              aria-selected={tab === key}
            >
              {label}
            </span>
          ))}
        </div>

        <div className="s-content">
          {tab === "general" && <GeneralTab />}
          {tab === "raster" && <RasterReference />}
          {tab === "health" && <HealthTab />}
          {tab === "about" && <AboutTab onClose={onClose} />}
          {tab === "shortcuts" && <ShortcutsTab />}
          {tab === "manual" && <ManualTab />}
          {tab === "developer" && <DeveloperTab />}
        </div>
      </div>
    </div>
  );
}

// ── General ──────────────────────────────────────────────────────────

function GeneralTab() {
  const { t } = useTranslation();
  const { data, mutate } = useSettings();
  const [showTemplates, setShowTemplates] = useState(false);

  async function patch(update: Partial<AppSettings>) {
    const next = await apiJson<AppSettings>("/api/settings/", {
      method: "PATCH",
      body: JSON.stringify(update),
    });
    void mutate(next, { revalidate: false });
    if (update.ui_language) await changeLanguage(update.ui_language);
  }

  if (!data) return <div className="s-loading">{t("common.loading")}</div>;

  return (
    <>
      <div className="s-row">
        <div className="s-lbl">{t("settings.ui_language")}</div>
        <div className="s-control">
          <select
            className="s-select"
            value={data.ui_language}
            onChange={(e) => patch({ ui_language: e.target.value as "en" | "de" })}
          >
            <option value="en">{t("common.english")}</option>
            <option value="de">{t("common.german")}</option>
          </select>
        </div>
      </div>

      <div className="s-row">
        <div className="s-lbl">{t("settings.claude_model")}</div>
        <div className="s-control">
          <select
            className="s-select"
            value={data.claude_model}
            onChange={(e) => patch({ claude_model: e.target.value })}
          >
            <option value="claude-opus-4-8">Claude Opus 4.8</option>
            <option value="claude-sonnet-4-6">Claude Sonnet 4.6</option>
          </select>
        </div>
      </div>

      <div className="s-row">
        <div className="s-lbl">{t("settings.manage_templates")}</div>
        <div className="s-control">
          <button
            type="button"
            className="s-btn"
            onClick={() => setShowTemplates(true)}
          >
            {t("settings.manage_templates")}
          </button>
        </div>
      </div>

      {showTemplates && <TemplateManager onClose={() => setShowTemplates(false)} />}
    </>
  );
}

// ── Health ───────────────────────────────────────────────────────────

interface HealthData {
  ok: boolean;
  db: string;
  version: string;
}

type ServiceStatus = "idle" | "loading" | "ok" | "error";

const SERVICES: { key: string; label: string }[] = [
  { key: "azure",        label: "settings.health_azure" },
  { key: "anthropic",    label: "settings.health_anthropic" },
  { key: "languagetool", label: "settings.health_languagetool" },
];

function HealthTab() {
  const { t } = useTranslation();
  const [healthData, setHealthData] = useState<HealthData | null>(null);
  const [statuses, setStatuses] = useState<Record<string, ServiceStatus>>({
    azure: "idle", anthropic: "idle", languagetool: "idle",
  });
  const [bundling, setBundling] = useState(false);
  const [bundleStatus, setBundleStatus] = useState<string | null>(null);

  async function checkService(key: string) {
    setStatuses((prev) => ({ ...prev, [key]: "loading" }));
    try {
      const result = await apiJson<HealthData>("/api/health");
      setHealthData(result);
      setStatuses((prev) => ({ ...prev, [key]: result.ok ? "ok" : "error" }));
    } catch {
      setStatuses((prev) => ({ ...prev, [key]: "error" }));
    }
  }

  useEffect(() => {
    for (const s of SERVICES) void checkService(s.key);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function downloadBundle() {
    setBundling(true);
    setBundleStatus(t("diagnostic.downloading"));
    try {
      const res = await apiFetch("/api/diagnostic/bundle", { method: "POST" });
      if (!res.ok) throw new Error(`status ${res.status}`);
      const blob = await res.blob();
      const stamp = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `ophelia-report-${stamp}.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      setBundleStatus(t("diagnostic.downloaded"));
    } catch {
      setBundleStatus(t("diagnostic.failed"));
    } finally {
      setBundling(false);
    }
  }

  void healthData;

  return (
    <>
      {SERVICES.map(({ key, label }) => (
        <div key={key} className="s-row">
          <div className="s-lbl">{t(label)}</div>
          <div className="s-control">
            <span className="s-pill">
              <span className={`s-indicator s-indicator--${statuses[key]}`} />
              {statuses[key] === "loading" ? t("settings.health_checking") : statuses[key] === "ok" ? t("settings.health_ok") : statuses[key] === "error" ? t("settings.health_error") : "—"}
            </span>
            <button
              type="button"
              className="s-btn s-btn--sm"
              disabled={statuses[key] === "loading"}
              onClick={() => void checkService(key)}
            >
              {t("settings.health_check_btn")}
            </button>
          </div>
        </div>
      ))}
      <div className="s-row s-row--actions">
        <button type="button" className="s-btn" onClick={downloadBundle} disabled={bundling}>
          {t("settings.report_a_problem")}
        </button>
        {bundleStatus && <span className="s-status">{bundleStatus}</span>}
      </div>
    </>
  );
}

// ── About ────────────────────────────────────────────────────────────

function AboutTab({ onClose }: { onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [data, setData] = useState<HealthData | null>(null);
  const [showReset, setShowReset] = useState(false);

  useEffect(() => {
    void apiJson<HealthData>("/api/health").then(setData);
  }, []);

  async function handleLogout() {
    try {
      await apiFetch("/api/auth/logout", { method: "POST" });
    } catch {
      // Cookie is cleared server-side regardless.
    }
    navigate("/login", { replace: true });
  }

  return (
    <div className="s-about">
      <p className="s-about-desc">{t("settings.about_description")}</p>

      <div className="s-row">
        <div className="s-lbl">{t("settings.about_version")}</div>
        <div className="s-control">
          <span className="s-readonly">{data?.version ?? "—"}</span>
        </div>
      </div>
      <div className="s-row s-row--actions">
        <button type="button" className="s-btn s-btn--accent" onClick={handleLogout}>
          {t("settings.logout")}
        </button>
      </div>

      <div className="s-danger-zone">
        <div className="s-danger-zone-header">
          <span className="s-danger-zone-title">{t("settings.danger_zone_title")}</span>
          <span className="s-danger-zone-desc">{t("settings.danger_zone_desc")}</span>
        </div>
        {!showReset ? (
          <button type="button" className="s-btn s-btn--danger" onClick={() => setShowReset(true)}>
            {t("settings.reset_database")}
          </button>
        ) : (
          <ResetZone onCancel={() => setShowReset(false)} onClose={onClose} />
        )}
      </div>
    </div>
  );
}

// ── Reset zone ───────────────────────────────────────────────────────

function ResetZone({ onCancel, onClose }: { onCancel: () => void; onClose: () => void }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function handleReset(e: React.FormEvent) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch("/api/auth/reset", {
        method: "POST",
        body: JSON.stringify({ password }),
      });
      if (res.ok) {
        onClose();
        navigate("/login", { replace: true });
        return;
      }
      setError(res.status === 401 ? t("settings.reset_error_password") : t("settings.reset_error_failed"));
    } catch {
      setError(t("settings.reset_error_network"));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="s-reset-zone">
      <p className="s-reset-warning">{t("settings.reset_database_warning")}</p>
      <form className="s-reset-form" onSubmit={handleReset} autoComplete="off">
        <label className="s-reset-label" htmlFor="s-reset-pw">
          {t("settings.reset_database_password_prompt")}
        </label>
        <input
          id="s-reset-pw"
          type="password"
          className="s-reset-input"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder={t("settings.password_placeholder")}
          autoFocus
          disabled={busy}
        />
        {error && <div className="s-reset-error">{error}</div>}
        <div className="s-reset-actions">
          <button
            type="submit"
            className="s-btn s-btn--danger"
            disabled={busy || password.length === 0}
          >
            {t("settings.reset_database_confirm_btn")}
          </button>
          <button type="button" className="s-btn" onClick={onCancel} disabled={busy}>
            {t("common.cancel")}
          </button>
        </div>
      </form>
    </div>
  );
}

// ── Developer ────────────────────────────────────────────────────────

function DeveloperTab() {
  const { t } = useTranslation();
  const { data, mutate } = useSettings();
  const [localThreshold, setLocalThreshold] = useState<string>("");
  const [localDeduction, setLocalDeduction] = useState<string>("");
  const [clearingTour, setClearingTour] = useState(false);
  const tourActive = useTourStore((s) => s.active);
  const endTourStore = useTourStore((s) => s.end);
  const showOcrConfidence = useDevStore((s) => s.showOcrConfidence);
  const setShowOcrConfidence = useDevStore((s) => s.setShowOcrConfidence);

  useEffect(() => {
    if (data) {
      setLocalThreshold(String(data.ocr_confidence_threshold ?? 0.5));
      setLocalDeduction(String(data.default_point_deduction ?? 1));
    }
  }, [data]);

  async function saveThreshold() {
    const val = parseFloat(localThreshold);
    if (isNaN(val) || val < 0 || val > 1) return;
    const next = await apiJson<AppSettings>("/api/settings/", {
      method: "PATCH",
      body: JSON.stringify({ ocr_confidence_threshold: val }),
    });
    void mutate(next, { revalidate: false });
  }

  async function saveDeduction() {
    const val = parseFloat(localDeduction);
    if (isNaN(val) || val < 0) return;
    const next = await apiJson<AppSettings>("/api/settings/", {
      method: "PATCH",
      body: JSON.stringify({ default_point_deduction: val }),
    });
    void mutate(next, { revalidate: false });
  }

  async function onClearTour() {
    setClearingTour(true);
    try {
      if (tourActive) { endTour(); endTourStore(); }
      await cleanupTour();
      toast(t("tour.cleared"));
    } catch {
      toast(t("common.error"));
    } finally {
      setClearingTour(false);
    }
  }

  if (!data) return <div className="s-loading">{t("common.loading")}</div>;

  return (
    <>
      <div className="s-row">
        <div className="s-lbl">{t("tour.clear_tour_data")}</div>
        <div className="s-control">
          <button
            type="button"
            className="s-btn s-btn--outline"
            disabled={clearingTour}
            onClick={() => void onClearTour()}
          >
            {clearingTour ? t("tour.clearing") : t("tour.clear_tour_data")}
          </button>
        </div>
      </div>
      <div className="s-row">
        <div className="s-lbl">
          OCR confidence threshold
          <div className="s-lbl-hint">
            Words with Azure confidence below this value are flagged for review.
            Lower = fewer flags, higher = more flags. Range 0–1.
          </div>
        </div>
        <div className="s-control">
          <input
            type="number"
            className="s-number-input"
            min={0}
            max={1}
            step={0.05}
            value={localThreshold}
            onChange={(e) => setLocalThreshold(e.target.value)}
            onBlur={saveThreshold}
          />
        </div>
      </div>
      <div className="s-row">
        <div className="s-lbl">
          Default point deduction
          <div className="s-lbl-hint">
            Pre-filled deduction when accepting a language, content, or note annotation.
            Teachers can still adjust per card.
          </div>
        </div>
        <div className="s-control">
          <input
            type="number"
            className="s-number-input"
            min={0}
            step={0.5}
            value={localDeduction}
            onChange={(e) => setLocalDeduction(e.target.value)}
            onBlur={saveDeduction}
          />
        </div>
      </div>
      <div className="s-row">
        <div className="s-lbl">
          Show OCR confidence
          <div className="s-lbl-hint">
            Overlay Azure confidence scores on each word box in the paper view. Persists across reloads.
          </div>
        </div>
        <div className="s-control">
          <button
            type="button"
            className={`s-btn ${showOcrConfidence ? "s-btn--accent" : "s-btn--outline"}`}
            onClick={() => setShowOcrConfidence(!showOcrConfidence)}
          >
            {showOcrConfidence ? "On" : "Off"}
          </button>
        </div>
      </div>
    </>
  );
}

// ── Manual ───────────────────────────────────────────────────────────

function ManualTab() {
  const { t } = useTranslation();
  return (
    <div className="s-manual">
      <p className="s-about-desc">{t("settings.manual_desc")}</p>
      <div className="s-row s-row--actions">
        <a
          className="s-btn s-btn--accent"
          href="/manual.html"
          target="_blank"
          rel="noopener noreferrer"
        >
          {t("settings.manual_open")} ↗
        </a>
      </div>
    </div>
  );
}

// ── Shortcuts ────────────────────────────────────────────────────────

interface ShortcutRow { keys: string[]; i18nKey: string }

const GLOBAL: ShortcutRow[] = [
  { keys: ["Esc"], i18nKey: "shortcut.esc" },
  { keys: ["⌘", "K"], i18nKey: "shortcut.cmd_k" },
  { keys: ["T"], i18nKey: "shortcut.t" },
];
const TREE: ShortcutRow[] = [
  { keys: ["↑", "↓"], i18nKey: "shortcut.tree_arrow" },
  { keys: ["Enter"], i18nKey: "shortcut.tree_enter" },
  { keys: ["Space"], i18nKey: "shortcut.tree_space" },
  { keys: ["⌘", "N"], i18nKey: "shortcut.tree_new" },
  { keys: ["⌘", "I"], i18nKey: "shortcut.tree_import" },
];
const REVIEW: ShortcutRow[] = [
  { keys: ["J", "K"], i18nKey: "shortcut.review_jk" },
  { keys: ["A"], i18nKey: "shortcut.review_a" },
  { keys: ["D"], i18nKey: "shortcut.review_d" },
  { keys: ["E"], i18nKey: "shortcut.review_e" },
  { keys: ["C"], i18nKey: "shortcut.review_c" },
  { keys: ["←", "→"], i18nKey: "shortcut.review_arrow" },
  { keys: ["R"], i18nKey: "shortcut.review_r" },
  { keys: ["M"], i18nKey: "shortcut.review_m" },
  { keys: ["⌘", "F"], i18nKey: "shortcut.review_cmd_f" },
  { keys: ["⌘", "Enter"], i18nKey: "shortcut.review_cmd_enter" },
  { keys: ["⌘", "E"], i18nKey: "shortcut.review_cmd_e" },
];

function ShortcutsTab() {
  const { t } = useTranslation();
  return (
    <div className="s-shortcuts">
      <ShortcutSection title={t("navigator.title")} rows={[...GLOBAL, ...TREE]} />
      <ShortcutSection title={t("paper_review.scan")} rows={REVIEW} />
    </div>
  );
}

function ShortcutSection({ title, rows }: { title: string; rows: ShortcutRow[] }) {
  const { t } = useTranslation();
  return (
    <div className="s-shortcut-section">
      <div className="s-shortcut-section-title">{title}</div>
      {rows.map((row, i) => (
        <div key={i} className="s-shortcut-row">
          <span className="s-shortcut-keys">
            {row.keys.map((k, j) => <kbd key={j}>{k}</kbd>)}
          </span>
          <span className="s-shortcut-desc">{t(row.i18nKey)}</span>
        </div>
      ))}
    </div>
  );
}
